import { useNavigate, useSearch } from "@tanstack/react-router";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";

import { useContractsRecord } from "../useContractsRecord";
import { RecordLink } from "./RecordLink";
import { useChurnDetail, type KpiChurnDetail, type KpiMonthRange } from "../api";
import type { KpiSearch } from "..";
import type { KpiPeriod } from "./PeriodSelector";
import { fmtArr, fmtUsd0 } from "../format";

export function ChurnDrawer({
  period,
  range,
  periodLabel,
}: {
  period: KpiPeriod;
  range: KpiMonthRange;
  periodLabel: string;
}) {
  const navigate = useNavigate();
  const search = useSearch({ from: "/kpi" });
  const open = search.tile === "churn";
  const expanded = !!search.expanded;

  const go = (next: Partial<KpiSearch>) =>
    navigate({ to: "/kpi", search: (prev: KpiSearch) => ({ ...prev, ...next }) });

  const detail = useChurnDetail(period, range, open);

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(v) => {
        if (!v && !expanded) go({ tile: undefined, expanded: undefined });
      }}
      expanded={expanded}
      onExpandedChange={(exp) => open && go({ expanded: exp ? true : undefined })}
      onClose={() => {
        if (expanded) go({ expanded: undefined });
        else go({ tile: undefined, expanded: undefined });
      }}
      title="Churn & Contraction"
      subtitle={periodLabel}
    >
      {detail.isLoading ? (
        <div className="p-6 text-sm text-muted-foreground">Loading…</div>
      ) : !detail.data?.available ? (
        <div className="p-6 text-sm text-muted-foreground">No data for this period.</div>
      ) : (
        <ChurnView data={detail.data} expanded={expanded} />
      )}
    </EntityDrawer>
  );
}

function ChurnView({ data, expanded }: { data: KpiChurnDetail; expanded: boolean }) {
  const { openAccount } = useContractsRecord();
  const t = data.totals;
  const churned = expanded ? (data.churned ?? []) : (data.churned ?? []).slice(0, 10);
  const contracted = expanded ? (data.contracted ?? []) : (data.contracted ?? []).slice(0, 10);
  return (
    <div>
      <div className="border-b border-border p-4">
        <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
          Total lost ARR (churn + contraction)
        </div>
        <div className="text-3xl font-extrabold tabular-nums text-danger">
          −{fmtArr(t?.total_arr ?? 0, 0)}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3 text-xs">
          <Mini label={`Churn (${t?.churned_count ?? 0})`} value={`−${fmtUsd0(t?.churn_arr)}`} />
          <Mini label={`Contraction (${t?.contracted_count ?? 0})`} value={`−${fmtUsd0(t?.contraction_arr)}`} />
          <Mini
            label="Net expansion vs churn"
            value={
              t?.net_expansion_arr != null
                ? `${t.net_expansion_arr >= 0 ? "+" : "−"}${fmtUsd0(Math.abs(t.net_expansion_arr))}`
                : "—"
            }
          />
        </div>
      </div>

      <div className="p-4">
        <SectionTitle>Churned</SectionTitle>
        <table className="mt-2 w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="px-2 py-1.5 text-left font-semibold">Account</th>
              {expanded && <th className="px-2 py-1.5 text-left font-semibold">Month</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Last ARR</th>
              <th className="px-2 py-1.5 text-right font-semibold">Lost</th>
            </tr>
          </thead>
          <tbody>
            {churned.map((c) => (
              <tr key={`${c.qb_id}-${c.month}`} className="border-b border-border/60">
                <td className="px-2 py-1.5">
                  <RecordLink
                    label={c.account_name}
                    onOpen={expanded && c.account_id ? () => openAccount(c.account_id) : undefined}
                  />
                </td>
                {expanded && <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{c.month}</td>}
                <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                  {fmtArr(c.prior_arr, 0)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-danger">
                  −{fmtArr(c.churn_arr, 0)}
                </td>
              </tr>
            ))}
            {churned.length === 0 && <Empty cols={expanded ? 4 : 3} />}
          </tbody>
        </table>
      </div>

      <div className="border-t border-border p-4">
        <SectionTitle>Contracted</SectionTitle>
        <table className="mt-2 w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border">
              <th className="px-2 py-1.5 text-left font-semibold">Account</th>
              {expanded && <th className="px-2 py-1.5 text-left font-semibold">Month</th>}
              <th className="px-2 py-1.5 text-right font-semibold">Prior → New</th>
              <th className="px-2 py-1.5 text-right font-semibold">Contraction</th>
            </tr>
          </thead>
          <tbody>
            {contracted.map((c) => (
              <tr key={`${c.qb_id}-${c.month}`} className="border-b border-border/60">
                <td className="px-2 py-1.5">
                  <RecordLink
                    label={c.account_name}
                    onOpen={expanded && c.account_id ? () => openAccount(c.account_id) : undefined}
                  />
                </td>
                {expanded && <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{c.month}</td>}
                <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">
                  {fmtArr(c.prior_arr, 0)} → {fmtArr(c.new_arr, 0)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-danger">
                  −{fmtArr(c.contraction_arr, 0)}
                </td>
              </tr>
            ))}
            {contracted.length === 0 && <Empty cols={expanded ? 4 : 3} />}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">
        {label}
      </div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Empty({ cols }: { cols: number }) {
  return (
    <tr>
      <td colSpan={cols} className="px-2 py-6 text-center text-sm text-muted-foreground">
        None in this period.
      </td>
    </tr>
  );
}
